import React, { useState, useEffect } from 'react';
import { booksAPI, departmentsAPI, categoriesAPI } from '../services/api';

function AddBookModal({ onClose, onBookAdded }) {
  const [formData, setFormData] = useState({
    book_name: '',
    author: '',
    department_id: '',
    category_id: '',
    condition: 'חדש',
    owner: 'פרטי עפולה',
    is_new: false,
    notes: ''
  });
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newDepartment, setNewDepartment] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [duplicateBook, setDuplicateBook] = useState(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

  useEffect(() => {
    loadDepartments();
    loadCategories();
  }, []);

  const loadDepartments = async () => {
    try {
      const response = await departmentsAPI.getAll();
      setDepartments(response.data);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleAddDepartment = async () => {
    if (!newDepartment.trim()) {
      alert('אנא הזן שם מחלקה');
      return;
    }

    try {
      // Generate code based on existing departments (2 digits)
      const code = String((departments.length + 1) * 10).padStart(2, '0');
      const response = await departmentsAPI.add({ code, name: newDepartment });
      setDepartments([...departments, response.data]);
      setNewDepartment('');
      setShowAddDepartment(false);
      setFormData(prev => ({ ...prev, department_id: response.data.id }));
    } catch (error) {
      console.error('Error adding department:', error);
      alert('שגיאה בהוספת מחלקה');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      alert('אנא הזן שם קטגוריה');
      return;
    }

    try {
      // Generate code based on existing categories (2 digits)
      const code = String((categories.length + 1) * 10).padStart(2, '0');
      const response = await categoriesAPI.add({ code, name: newCategory });
      setCategories([...categories, response.data]);
      setNewCategory('');
      setShowAddCategory(false);
    } catch (error) {
      console.error('Error adding category:', error);
      alert('שגיאה בהוספת קטגוריה');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // וליידציה
    if (!formData.book_name.trim() || formData.book_name.trim().length < 2) {
      alert('שם הספר חייב להכיל לפחות 2 תווים');
      return;
    }
    
    if (!formData.author.trim() || formData.author.trim().length < 2) {
      alert('שם המחבר חייב להכיל לפחות 2 תווים');
      return;
    }

    if (!formData.department_id) {
      alert('אנא בחר מחלקה');
      return;
    }

    try {
      setLoading(true);
      
      // בדוק אם ספר דומה כבר קיים
      const checkResponse = await booksAPI.checkDuplicate({
        book_name: formData.book_name,
        author: formData.author,
        category_id: formData.category_id || null
      });

      if (checkResponse.data.exists) {
        // ספר קיים - הצג dialog לאישור
        setDuplicateBook(checkResponse.data.book);
        setShowDuplicateDialog(true);
        return;
      }

      // ספר לא קיים - הוסף ספר חדש
      await booksAPI.create(formData);
      onBookAdded();
    } catch (error) {
      console.error('Error adding book:', error);
      alert('שגיאה בהוספת הספר');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCopy = async () => {
    try {
      setLoading(true);
      await booksAPI.addBookCopy({
        existing_book_id: duplicateBook.id,
        copies: 1
      });
      setShowDuplicateDialog(false);
      setDuplicateBook(null);
      onBookAdded();
    } catch (error) {
      console.error('Error adding book copy:', error);
      alert('שגיאה בהוספת עותק');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewAnyway = async () => {
    try {
      setLoading(true);
      await booksAPI.create(formData);
      setShowDuplicateDialog(false);
      setDuplicateBook(null);
      onBookAdded();
    } catch (error) {
      console.error('Error adding book:', error);
      alert('שגיאה בהוספת הספר');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>➕ הוסף ספר חדש</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>שם הספר *</label>
            <input
              type="text"
              name="book_name"
              value={formData.book_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>מחבר *</label>
            <input
              type="text"
              name="author"
              value={formData.author}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>מחלקה *</label>
            <div style={{ display: 'flex', gap: '5px' }}>
              <select
                name="department_id"
                value={formData.department_id}
                onChange={handleChange}
                required
                style={{ flex: 1 }}
              >
                <option value="">-- בחר מחלקה --</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name} ({dept.code})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowAddDepartment(!showAddDepartment)}
              >
                +
              </button>
            </div>
            {showAddDepartment && (
              <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
                <input
                  type="text"
                  placeholder="שם מחלקה חדשה"
                  value={newDepartment}
                  onChange={(e) => setNewDepartment(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleAddDepartment}
                >
                  הוסף
                </button>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>קטגוריה (אופציונלי)</label>
            <div style={{ display: 'flex', gap: '5px' }}>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                style={{ flex: 1 }}
              >
                <option value="">-- אין קטגוריה --</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name} ({cat.code})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowAddCategory(!showAddCategory)}
              >
                +
              </button>
            </div>
            {showAddCategory && (
              <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
                <input
                  type="text"
                  placeholder="שם קטגוריה חדשה"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleAddCategory}
                >
                  הוסף
                </button>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>מצב הספר</label>
            <select
              name="condition"
              value={formData.condition}
              onChange={handleChange}
            >
              <option value="חדש">חדש</option>
              <option value="טוב">טוב</option>
              <option value="משומש">משומש</option>
              <option value="בלוי">בלוי</option>
            </select>
          </div>

          <div className="form-group">
            <label>בעלים</label>
            <input
              type="text"
              name="owner"
              value={formData.owner}
              onChange={handleChange}
              placeholder="פרטי עפולה"
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                name="is_new"
                checked={formData.is_new}
                onChange={handleChange}
              />
              {' '}האם הספר חדש?
            </label>
          </div>

          <div className="form-group">
            <label>הערות</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="הערות עלספר..."
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-danger" onClick={onClose}>
              ביטול
            </button>
            <button type="submit" className="btn btn-success" disabled={loading}>
              {loading ? 'מוסיף...' : 'הוסף'}
            </button>
          </div>
        </form>
      </div>

      {/* Dialog for duplicate book confirmation */}
      {showDuplicateDialog && duplicateBook && (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>⚠️ ספר קיים</h2>
            </div>
            
            <div style={{ padding: '20px', lineHeight: '1.6' }}>
              <p>ספר בשם <strong>"{duplicateBook.book_name}"</strong> של <strong>{duplicateBook.author}</strong> כבר קיים במערכת.</p>
              
              <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
                <h4>פרטי הספר הקיים:</h4>
                <div><strong>ברקוד:</strong> {duplicateBook.barcode}</div>
                <div><strong>מצב:</strong> {duplicateBook.condition}</div>
                <div><strong>בעלים:</strong> {duplicateBook.owner}</div>
                <div><strong>חדש:</strong> {duplicateBook.is_new ? 'כן' : 'לא'}</div>
              </div>

              <div style={{ marginBottom: '20px', padding: '10px', background: '#fff3cd', borderRadius: '5px', borderLeft: '4px solid #ffc107' }}>
                <strong>הבחירה שלך:</strong>
                <p>האם תרצה להוסיף עותק נוסף עם אותן הפרמטרים, או להוסיף ספר חדש (שונה)?</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '20px' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowDuplicateDialog(false);
                  setDuplicateBook(null);
                }}
                disabled={loading}
              >
                חזור לעריכה
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleAddCopy}
                disabled={loading}
              >
                ➕ הוסף עותק
              </button>
              <button 
                className="btn btn-warning"
                onClick={handleAddNewAnyway}
                disabled={loading}
              >
                ➕ הוסף ספר חדש בכל זאת
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddBookModal;
